/**
 * Activation Quiz Module
 * Mini quiz to find the right Fitbar based on user activities
 */

export class ActivationQuiz {
  constructor() {
    this.form = document.getElementById('activationQuiz');
    this.loadingState = document.getElementById('activationLoading');
    this.resultState = document.getElementById('activationResult');
    this.sectionTitle = document.getElementById('activationSectionTitle');
    this.cards = document.querySelectorAll('.activation-card');
    this.progressFill = document.querySelector('.activation-progress__fill');
    this.progressCurrent = document.querySelector('.activation-progress__current');
    this.progressTotal = document.querySelector('.activation-progress__total');

    this.currentQuestion = 1;
    this.totalQuestions = this.cards.length; // Dynamically set based on number of cards
    this.answers = {};

    // Store original section title for reset
    this.originalSectionTitle = this.sectionTitle ? this.sectionTitle.innerHTML : '';

    // Result mappings loaded from window.fitbarQuizResults (defined in PHP)
    // This allows easy modification of results without rebuilding JS
    this.resultMappings = window.fitbarQuizResults || {};

    this.init();
  }

  init() {
    if (!this.form) return;

    this.bindEvents();
    this.updateCardStates();
    this.updateProgress();
  }

  bindEvents() {
    // Yes/No button clicks
    const yesBtn = this.form.querySelector('.activation-btn--check');
    const noBtn = this.form.querySelector('.activation-btn--times');

    if (yesBtn) {
      yesBtn.addEventListener('click', () => this.handleAnswer('yes'));
      // Hover animations for check button - tilt card to right
      yesBtn.addEventListener('mouseenter', () => this.handleHover('right'));
      yesBtn.addEventListener('mouseleave', () => this.handleHoverEnd());
    }

    if (noBtn) {
      noBtn.addEventListener('click', () => this.handleAnswer('no'));
      // Hover animations for times button - tilt card to left
      noBtn.addEventListener('mouseenter', () => this.handleHover('left'));
      noBtn.addEventListener('mouseleave', () => this.handleHoverEnd());
    }

    // Add hover class to activation-choices when hovering on activation-btn
    const activationBtns = this.form.querySelectorAll('.activation-btn');
    activationBtns.forEach((btn) => {
      const choicesWrapper = btn.closest('.activation-quiz');
      if (choicesWrapper) {
        btn.addEventListener('mouseenter', () => {
          if (btn.classList.contains('activation-btn--times')) {
            choicesWrapper.classList.add('choice-times-hover');
          } else if (btn.classList.contains('activation-btn--check')) {
            choicesWrapper.classList.add('choice-check-hover');
          }
        });
        btn.addEventListener('mouseleave', () => {
          choicesWrapper.classList.remove('choice-times-hover', 'choice-check-hover');
        });
      }
    });

    // Reset button
    const resetBtn = document.getElementById('btnCobaUlang');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetQuiz());
    }
  }

  handleHover(direction) {
    const activeCard = document.querySelector('.activation-card.is-active');
    if (!activeCard) return;

    // Add transition for smooth animation
    activeCard.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';

    if (direction === 'left') {
      // Tilt to left when hovering X button
      activeCard.style.transform = 'rotate(-10deg) translateX(-162px) translateY(-29px)';
    } else if (direction === 'right') {
      // Tilt to right when hovering check button
      activeCard.style.transform = 'rotate(10deg) translateX(162px) translateY(-29px)';
    }
  }

  handleHoverEnd() {
    const activeCard = document.querySelector('.activation-card.is-active');
    if (!activeCard) return;

    // Reset to original position
    activeCard.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
    activeCard.style.transform = 'rotate(0deg) translateX(0) translateY(0)';
  }

  handleAnswer(answer) {
    // Record the answer
    this.answers[`q${this.currentQuestion}`] = answer;

    // Check the corresponding radio input
    const radioInput = document.getElementById(`q${this.currentQuestion}-${answer}`);
    if (radioInput) {
      radioInput.checked = true;
    }

    // Animate the current card out
    this.animateCardOut(answer);

    // Move to next question or show result
    setTimeout(() => {
      if (this.currentQuestion < this.totalQuestions) {
        this.currentQuestion++;
        this.updateCardStates();
        this.updateProgress();
      } else {
        this.showLoading();
      }
    }, 400);
  }

  animateCardOut(answer) {
    const currentCard = document.querySelector(`.activation-card[data-card="${this.currentQuestion}"]`);
    if (!currentCard) return;

    currentCard.classList.add('is-answered');

    // Force reflow to ensure transition works
    void currentCard.offsetHeight;

    // Add transition for smooth animation
    currentCard.style.transition = 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease';

    // Animate based on answer direction
    if (answer === 'yes') {
      // Slide to right and fade out
      currentCard.style.transform = 'rotate(20deg) translateX(300px) translateY(-50px)';
      currentCard.style.opacity = '0';
    } else {
      // Slide to left and fade out
      currentCard.style.transform = 'rotate(-20deg) translateX(-300px) translateY(-50px)';
      currentCard.style.opacity = '0';
    }
  }

  updateCardStates() {
    this.cards.forEach((card, index) => {
      const cardNum = index + 1;

      // Reset styles
      card.style.transform = '';
      card.style.opacity = '';
      card.classList.remove('is-active', 'is-next', 'is-answered');

      if (cardNum === this.currentQuestion) {
        // Current active card
        card.classList.add('is-active');
        card.style.zIndex = '8';
        card.style.transform = 'rotate(0deg) translateX(0) translateY(0)';
        card.style.opacity = '1';
      } else if (cardNum === this.currentQuestion + 1) {
        // Next card (visible behind)
        card.classList.add('is-next');
        card.style.zIndex = '7';
        card.style.transform = 'rotate(0deg) translateX(0px) translateY(0px)';
        card.style.opacity = '1';
      } else if (cardNum < this.currentQuestion) {
        // Already answered - hide
        card.style.zIndex = '1';
        card.style.transform = 'rotate(0deg) translateX(0) translateY(20px)';
        card.style.opacity = '0';
        card.classList.add('is-answered');
      } else {
        // Future cards - hide
        card.style.zIndex = '1';
        card.style.transform = 'rotate(0deg) translateX(0) translateY(20px)';
        card.style.opacity = '0';
      }
    });
  }

  updateProgress() {
    if (this.progressCurrent) {
      this.progressCurrent.textContent = this.currentQuestion;
    }

    // Update circular progress
    if (this.progressFill) {
      const circumference = 2 * Math.PI * 26; // 2 * PI * radius(26)
      const progress = this.currentQuestion / this.totalQuestions;
      const offset = circumference - (progress * circumference);
      this.progressFill.style.strokeDashoffset = offset;
    }
  }

  /**
   * Update section title with fade animation
   * @param {string} html - The HTML content to set
   */
  updateSectionTitle(html) {
    if (!this.sectionTitle) return;

    // Reset animation
    this.sectionTitle.style.animation = 'none';
    void this.sectionTitle.offsetHeight; // Trigger reflow

    // Update content
    this.sectionTitle.innerHTML = html;

    // Apply animation
    this.sectionTitle.style.animation = 'fadeInUp 0.3s ease';
  }

  showLoading() {
    // Hide quiz form
    this.form.style.display = 'none';

    // Show loading state
    if (this.loadingState) {
      this.loadingState.style.display = 'flex';
    }

    // Update section title to show loading text with animation
    this.updateSectionTitle('<strong>Tunggu Sebentar</strong>');

    // Calculate and show result after delay
    setTimeout(() => {
      this.calculateAndShowResult();
    }, 2000);
  }

  calculateAndShowResult() {
    // Hide loading
    if (this.loadingState) {
      this.loadingState.style.display = 'none';
    }

    // Calculate result based on answers
    const result = this.calculateResult();

    // Update result UI
    const resultProduct = document.getElementById('resultProduct');
    const resultType = document.getElementById('resultType');
    const resultDesc = document.getElementById('resultDesc');
    const resultImage = document.getElementById('resultImage');

    if (resultProduct) resultProduct.textContent = result.product;
    if (resultType) resultType.textContent = result.type;
    if (resultDesc) resultDesc.textContent = result.desc;
    if (resultImage) resultImage.src = result.image;

    // Update section title to show result text with animation
    this.updateSectionTitle('<strong>Rekomendasi</strong> untuk kamu');

    // Show result state
    if (this.resultState) {
      this.resultState.style.display = 'block';
    }
  }

  calculateResult() {
    // Calculate scores based on data-scores attributes
    const categoryScores = {};

    this.cards.forEach((card) => {
      const questionId = card.dataset.card;
      const answer = this.answers[`q${questionId}`];

      // Parse scores from data-scores attribute (JSON format)
      let scores = {};
      try {
        scores = JSON.parse(card.dataset.scores || '{}');
      } catch (e) {
        console.warn('Invalid scores data for card', questionId);
        return;
      }

      // Add scores if answer is 'yes'
      if (answer === 'yes') {
        Object.entries(scores).forEach(([category, points]) => {
          if (!categoryScores[category]) {
            categoryScores[category] = 0;
          }
          categoryScores[category] += points;
        });
      }
    });

    // Find category with highest score
    let winningCategory = 'balanced'; // default
    let maxScore = 0;

    Object.entries(categoryScores).forEach(([category, score]) => {
      if (score > maxScore) {
        maxScore = score;
        winningCategory = category;
      }
    });

    // If no yes answers, default to balanced
    if (maxScore === 0) {
      winningCategory = 'balanced';
    }

    return this.resultMappings[winningCategory] || this.resultMappings.balanced;
  }

  resetQuiz() {
    // Reset state
    this.currentQuestion = 1;
    this.answers = {};

    // Uncheck all radio inputs
    const radioInputs = this.form.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.checked = false;
    });

    // Hide result, show quiz
    if (this.resultState) {
      this.resultState.style.display = 'none';
    }
    this.form.style.display = 'block';

    // Reset section title to original text with animation
    this.updateSectionTitle(this.originalSectionTitle);

    // Reset card states
    this.updateCardStates();
    this.updateProgress();
  }
}

/**
 * Initialize Activation Quiz
 */
export function initActivationQuiz() {
  const quiz = new ActivationQuiz();
  return quiz;
}
